import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const References = () => {
  const [showReferences, setShowReferences] = useState(false);
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchReferences = async () => {
      setLoading(true);
      try {
        const res = await fetch('https://spaceportfolio-api.rajansudhan0.workers.dev/api/resume');
        const data = await res.json();
        const filtered = data.filter(item => item.type === 'reference');
        setReferences(filtered);
      } catch (err) {
        console.error('Failed to fetch references:', err);
        setReferences([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReferences();
  }, []);

  const handleShowReferences = () => {
    setShowReferences(true);
  };

  if (!loading && references.length === 0) return null;

  return (
    <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30"></div>
      <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-6 relative z-10">
        Professional References
      </h2>

      {!showReferences ? (
        <div className="text-center py-8 relative z-10">
          <div className="w-16 h-16 bg-accent/10 border border-accent/20 rounded-full flex items-center justify-center mx-auto mb-4 scale-110">
            <Icon name="Users" size={32} className="text-accent-light" />
          </div>
          <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
            References Available Upon Request
          </h3>
          <p className="text-text-secondary mb-6 max-w-md mx-auto">
            I’m fortunate to have worked with renowned professionals across ISRO, academia, and startups.
            Feel free to request direct references based on your interest.
          </p>

          <Button
            variant="primary"
            onClick={handleShowReferences}
            iconName="Eye"
            iconPosition="left"
            className="shadow-lg shadow-accent/20 px-8"
          >
            View References
          </Button>
          <p className="text-text-muted text-xs mt-4">
            References have provided consent for professional inquiries
          </p>
        </div>
      ) : (
        <div className="space-y-6 relative z-10">
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={20} className="text-success-light" />
              <p className="text-success-light font-medium text-sm">
                All references have provided consent for professional inquiries.
              </p>
            </div>
          </div>

          {loading ? (
             <div className="space-y-6">
                {[1, 2].map(i => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 animate-pulse">
                     <div className="h-6 bg-white/10 rounded w-1/3 mb-2"></div>
                     <div className="h-4 bg-accent/20 rounded w-1/4 mb-4"></div>
                     <div className="h-24 bg-white/5 rounded w-full"></div>
                  </div>
                ))}
             </div>
          ) : (
            references.map((ref) => {
              const email = ref.technologies?.[0] || "";
              const phone = ref.technologies?.[1] || "";
              const linkedIn = ref.technologies?.[2] || "";

              return (
                <div key={ref.id} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-accent/30 transition-smooth group">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-6">
                    <div className="flex-1">
                      <div className="mb-4">
                        <h3 className="font-heading font-bold text-xl text-text-primary mb-1 group-hover:text-accent transition-smooth">{ref.title}</h3>
                        <p className="text-accent font-semibold">{ref.location}</p>
                        <p className="text-text-secondary">{ref.organization}</p>
                        <p className="text-text-muted text-sm mt-1">{ref.duration}</p>
                      </div>

                      <div className="bg-white/5 rounded-lg p-4 mb-4 border border-white/5">
                        <h4 className="font-semibold text-text-primary mb-2">Testimonial:</h4>
                        <p className="text-text-secondary italic leading-relaxed">"{ref.description}"</p>
                      </div>
                    </div>

                    {email || phone || linkedIn ? (
                      <div className="lg:w-64 space-y-3">
                        <h4 className="font-semibold text-text-primary">Contact Information:</h4>
                        <div className="space-y-2">
                          {email && (
                            <div className="flex items-center space-x-2 group/link">
                              <Icon name="Mail" size={16} className="text-accent-light flex-shrink-0" />
                              <a href={`mailto:${email}`} className="text-text-secondary hover:text-accent-light text-sm transition-smooth truncate block max-w-full" title={email}>{email}</a>
                            </div>
                          )}
                          {phone && (
                            <div className="flex items-center space-x-2 group/link">
                              <Icon name="Phone" size={16} className="text-accent-light flex-shrink-0" />
                              <a href={`tel:${phone}`} className="text-text-secondary hover:text-accent-light text-sm transition-smooth truncate block max-w-full" title={phone}>{phone}</a>
                            </div>
                          )}
                          {linkedIn && (
                            <div className="flex items-center space-x-2 group/link">
                              <Icon name="Linkedin" size={16} className="text-accent-light flex-shrink-0" />
                              <a
                                href={linkedIn}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-text-secondary hover:text-accent-light text-sm transition-smooth truncate block max-w-full"
                                title={linkedIn}
                              >
                                LinkedIn Profile
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </section>
  );
};

export default References;
